import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { DataTableColumn, DataTableColumnType, FilterType } from '../../../../../../common/_custom/DataTable/DataTable.types';
import { parseINR } from '../../../RevenueTables/RevenueVendorExpenseTable/components/overlap.constants';
import { DATE_FORMATS } from '../../../../../../utils/dateUtils';

dayjs.extend(customParseFormat);

/**
 * Filter/sort behaviour for the Credit Summary table (polus `GET /cibil/table`).
 *
 * Why this map exists at all: polus sends `headers: [{name, key}]` and nothing else — no type
 * information — while every cell value in `cibilData` is a pre-formatted String. `"₹ 25,00,000"`
 * and `"Active"` are indistinguishable to the client, so the filter drawer cannot know whether a
 * column wants a tick-list, a date range or a min/max pair. This map supplies exactly that, keyed
 * by the header `key` polus already sends.
 *
 * It is merged ONTO the response headers (see buildCreditSummaryColumns) rather than replacing
 * them: the response stays the source of truth for which columns exist and in what order, so a
 * column polus adds later renders as an ordinary column instead of breaking the page.
 */

export const CREDIT_SUMMARY_FILTER_SOURCE_KEY = 'CREDIT_SUMMARY';

/** The dash polus writes for any value the bureau did not report (`CibilTableRow`). */
export const MISSING_CELL_VALUE = '-';

/** Format polus formats both date columns with — `DateUtil.DD_MM_YYYY_WITH_SPACE`. */
export const CREDIT_SUMMARY_DATE_FORMAT = DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY;

export enum CreditSummaryValueType {
    TEXT = 'TEXT',
    AMOUNT = 'AMOUNT',
    DATE = 'DATE',
    DURATION = 'DURATION',
}

type CreditSummaryColumnBehaviour = {
    valueType: CreditSummaryValueType;
    filterType?: FilterType;
    sortable?: boolean;
    /**
     * A sibling field on the row holding the raw, unformatted value — preferred over parsing the
     * display string. Only `sanctionDate` has one (`sanctionedDateActual`, a real Date that polus
     * already serializes and uses as its own tertiary sort key).
     */
    rawValueKey?: string;
    /** Rendered inside the min/max inputs by RangeFilterRenderer via `column.unit`. Keep to one
     *  character: the input's unit wrapper is squeezed to 10% width with text clipping, so anything
     *  longer ("Months") renders cut off. Longer units go in `labelSuffix` instead. */
    unit?: string;
    /** Appended to the filter card's label — e.g. "(Months)" — for units too long for the input. */
    labelSuffix?: string;
};

export const CREDIT_SUMMARY_COLUMNS: Record<string, CreditSummaryColumnBehaviour> = {
    // Categorical — options are the values actually present in the rows, see distinctColumnOptions.
    // NOTE `ownership` deliberately keeps its API key even though useDynamicFilterOptions assigns
    // owner options to any field starting with "owners" — `filterConfig.options` is applied after
    // that block, so ours wins. Do not rename the field to dodge it; it must match the payload.
    name: { valueType: CreditSummaryValueType.TEXT, filterType: FilterType.MULTI_SELECT },
    investorName: { valueType: CreditSummaryValueType.TEXT, filterType: FilterType.MULTI_SELECT },
    loanType: { valueType: CreditSummaryValueType.TEXT, filterType: FilterType.MULTI_SELECT },
    accountStatus: { valueType: CreditSummaryValueType.TEXT, filterType: FilterType.MULTI_SELECT },
    frequency: { valueType: CreditSummaryValueType.TEXT, filterType: FilterType.MULTI_SELECT },
    ownership: { valueType: CreditSummaryValueType.TEXT, filterType: FilterType.MULTI_SELECT },
    accountRemarks: { valueType: CreditSummaryValueType.TEXT, filterType: FilterType.MULTI_SELECT },

    // Dates — from/to range, sortable.
    lastReported: { valueType: CreditSummaryValueType.DATE, filterType: FilterType.DATE_RANGE, sortable: true },
    sanctionDate: {
        valueType: CreditSummaryValueType.DATE,
        filterType: FilterType.DATE_RANGE,
        sortable: true,
        rawValueKey: 'sanctionedDateActual',
    },

    // Amounts — min/max, sortable.
    sanctionedLimit: { valueType: CreditSummaryValueType.AMOUNT, filterType: FilterType.RANGE, sortable: true, unit: '₹' },
    outstandingAmount: { valueType: CreditSummaryValueType.AMOUNT, filterType: FilterType.RANGE, sortable: true, unit: '₹' },
    currentOverdue: { valueType: CreditSummaryValueType.AMOUNT, filterType: FilterType.RANGE, sortable: true, unit: '₹' },
    emi: { valueType: CreditSummaryValueType.AMOUNT, filterType: FilterType.RANGE, sortable: true, unit: '₹' },
    tenure: { valueType: CreditSummaryValueType.DURATION, filterType: FilterType.RANGE, sortable: true, labelSuffix: '(Months)' },

    // `repaymentHistory` ("Delays") is a run of month-by-month delay codes, not a scalar — neither
    // a tick-list nor a range means anything for it, so it gets no filter and no sort.
    repaymentHistory: { valueType: CreditSummaryValueType.TEXT },
};

/** True for a cell the bureau did not report — polus writes `-`, and `sanctionDate` can be null. */
export function isMissingCellValue(raw: unknown): boolean {
    if (raw === null || raw === undefined) return true;
    const trimmed = String(raw).trim();
    return trimmed === '' || trimmed === MISSING_CELL_VALUE;
}

/**
 * `"₹ 25,00,000"` -> `2500000`. Returns null for a missing or unparseable cell so callers can sort
 * and filter it as "no value" rather than as zero.
 *
 * The numeric conversion itself reuses `parseINR`, but we pre-validate instead of leaning on its
 * `|| 0` fallback: that fallback turns anything unparseable into a legitimate-looking ₹0.
 */
export function parseAmountCell(raw: unknown): number | null {
    if (isMissingCellValue(raw)) return null;
    const cleaned = String(raw).replace(/[₹,\s]/g, '');
    if (cleaned === '' || Number.isNaN(Number(cleaned))) return null;
    return parseINR(String(raw));
}

/**
 * `"20 Feb 2022"` -> epoch ms, matching the epoch bounds DateRangeFilterRenderer emits.
 * Prefers `rawValue` (polus's real Date) when the column declares one.
 */
export function parseDateCell(raw: unknown, rawValue?: unknown): number | null {
    if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
        const fromRaw = dayjs(rawValue as string);
        if (fromRaw.isValid()) return fromRaw.valueOf();
    }
    if (isMissingCellValue(raw)) return null;
    const parsed = dayjs(String(raw).trim(), CREDIT_SUMMARY_DATE_FORMAT, true);
    return parsed.isValid() ? parsed.valueOf() : null;
}

/** `"12 Months"` -> `12`. Numeric, so 9 sorts below 12 instead of lexically above it. */
export function parseDurationCell(raw: unknown): number | null {
    if (isMissingCellValue(raw)) return null;
    const matched = String(raw).match(/-?\d+(\.\d+)?/);
    return matched ? Number(matched[0]) : null;
}

/**
 * The comparable value behind a cell, or null when there is nothing to compare.
 * TEXT columns are neither sortable nor range-filterable, so they have no numeric form.
 */
export function readComparableCellValue(row: any, field: string): number | null {
    const behaviour = CREDIT_SUMMARY_COLUMNS[field];
    if (!behaviour) return null;

    switch (behaviour.valueType) {
        case CreditSummaryValueType.AMOUNT:
            return parseAmountCell(row?.[field]);
        case CreditSummaryValueType.DATE:
            return parseDateCell(row?.[field], behaviour.rawValueKey ? row?.[behaviour.rawValueKey] : undefined);
        case CreditSummaryValueType.DURATION:
            return parseDurationCell(row?.[field]);
        default:
            return null;
    }
}

/**
 * Tick-list options for a categorical column: the distinct values actually present in the rows.
 * Derived rather than hardcoded so the Investor Name list on a given company shows exactly the
 * lenders on that company's file — a fixed list would go stale the moment a new lender appeared.
 */
export function distinctColumnOptions(rows: any[], field: string): { label: string; value: string }[] {
    const seen = new Set<string>();
    (rows || []).forEach((row) => {
        const raw = row?.[field];
        if (isMissingCellValue(raw)) return;
        seen.add(String(raw).trim());
    });
    return Array.from(seen)
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({ label: value, value }));
}

/**
 * Builds the DataTableColumn list the shared filter drawer consumes, by merging the behaviour map
 * onto the headers polus sent. Unknown keys yield a plain column — no filter card, no sort icon.
 */
export function buildCreditSummaryColumns(headers: { name: string; key: string }[] | undefined, rows: any[]): DataTableColumn[] {
    return (headers || []).map((header) => {
        const behaviour = CREDIT_SUMMARY_COLUMNS[header?.key];

        const column = {
            field: header?.key,
            headerLabel: header?.name,
            headerOriginalLabel: header?.name,
            type: DataTableColumnType.TEXT,
        } as DataTableColumn;

        if (!behaviour?.filterType) return column;

        column.filterConfig = {
            enabled: true,
            filterType: behaviour.filterType,
            label: behaviour.labelSuffix ? `${header?.name} ${behaviour.labelSuffix}` : header?.name,
            ...(behaviour.filterType === FilterType.MULTI_SELECT && {
                options: distinctColumnOptions(rows, header.key),
                excludedValues: [MISSING_CELL_VALUE],
            }),
        };
        if (behaviour.unit) column.unit = behaviour.unit;

        return column;
    });
}

/** Whether a column carries a sort control. */
export function isSortableColumn(field: string): boolean {
    return !!CREDIT_SUMMARY_COLUMNS[field]?.sortable;
}
